/**
 * Admin → Product Management → Offerings
 *
 * CRUD over product offerings (plans). No hard delete — activate/deactivate
 * only (rows are referenced by orders). Follows the PersonasPage patterns:
 * useQuery + apiClient + dialog form with local state.
 *
 * Design: docs/technical-design-website-product.md §4.2
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Edit, Check, Loader2, Power, PowerOff } from 'lucide-react';
import { apiClient } from '@/services/api-client';
import type { AdminProductOffering, BillingType, CatalogStatus } from '@/types';

interface OfferingFormData {
  slug: string;
  name: string;
  planLabel: string;
  tagline: string;
  description: string;
  badge: string;
  priceDollars: string;
  billingType: BillingType;
  recurringPriceDollars: string;
  recurringInterval: 'month' | 'year';
  collectionPeriodMonths: string;
  storyLimit: string;
  featuresText: string; // one feature per line, order = display order
  includesOrb: boolean;
  displayOrder: string;
  status: CatalogStatus;
}

const emptyForm: OfferingFormData = {
  slug: '',
  name: '',
  planLabel: '',
  tagline: '',
  description: '',
  badge: '',
  priceDollars: '',
  billingType: 'one_time',
  recurringPriceDollars: '',
  recurringInterval: 'month',
  collectionPeriodMonths: '',
  storyLimit: '',
  featuresText: '',
  includesOrb: false,
  displayOrder: '0',
  status: 'available',
};

const formatPrice = (cents: number) =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const dollarsToCents = (value: string): number => Math.round(parseFloat(value) * 100);

function toPayload(form: OfferingFormData) {
  const recurring = form.billingType === 'first_year_then_recurring';
  return {
    name: form.name,
    planLabel: form.planLabel,
    tagline: form.tagline || null,
    description: form.description || null,
    badge: form.badge || null,
    priceCents: dollarsToCents(form.priceDollars),
    billingType: form.billingType,
    recurringPriceCents: recurring ? dollarsToCents(form.recurringPriceDollars) : null,
    recurringInterval: recurring ? form.recurringInterval : null,
    collectionPeriodMonths: form.collectionPeriodMonths
      ? parseInt(form.collectionPeriodMonths, 10)
      : null,
    storyLimit: form.storyLimit ? parseInt(form.storyLimit, 10) : null,
    features: form.featuresText
      .split('\n')
      .map((f) => f.trim())
      .filter(Boolean),
    includesOrb: form.includesOrb,
    displayOrder: parseInt(form.displayOrder, 10) || 0,
    status: form.status,
  };
}

export function ProductOfferingsPage() {
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selected, setSelected] = useState<AdminProductOffering | null>(null);
  const [form, setForm] = useState<OfferingFormData>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Inline banner above the table for failed activate/deactivate (review F11).
  const [tableError, setTableError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const {
    data: offerings,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['admin', 'product-offerings'],
    queryFn: async () => {
      const response = await apiClient.get<AdminProductOffering[]>(
        '/api/v1/admin/products/offerings'
      );
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  const set = <K extends keyof OfferingFormData>(key: K, value: OfferingFormData[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleCreate = () => {
    setForm(emptyForm);
    setIsEditing(false);
    setSelected(null);
    setError(null);
    setFormOpen(true);
  };

  const handleEdit = (offering: AdminProductOffering) => {
    setForm({
      slug: offering.slug,
      name: offering.name,
      planLabel: offering.planLabel,
      tagline: offering.tagline ?? '',
      description: offering.description ?? '',
      badge: offering.badge ?? '',
      priceDollars: (offering.priceCents / 100).toString(),
      billingType: offering.billingType,
      recurringPriceDollars:
        offering.recurringPriceCents != null ? (offering.recurringPriceCents / 100).toString() : '',
      recurringInterval: offering.recurringInterval ?? 'month',
      collectionPeriodMonths: offering.collectionPeriodMonths?.toString() ?? '',
      storyLimit: offering.storyLimit?.toString() ?? '',
      featuresText: offering.features.join('\n'),
      includesOrb: offering.includesOrb,
      displayOrder: offering.displayOrder.toString(),
      status: offering.status ?? 'available',
    });
    setIsEditing(true);
    setSelected(offering);
    setError(null);
    setFormOpen(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    const payload = toPayload(form);
    const response =
      isEditing && selected
        ? await apiClient.patch(`/api/v1/admin/products/offerings/${selected.id}`, payload)
        : await apiClient.post('/api/v1/admin/products/offerings', {
            ...payload,
            slug: form.slug,
          });

    setIsSaving(false);
    if (!response.success) {
      setError(response.error?.message || 'Save failed');
      return;
    }
    setFormOpen(false);
    setTableError(null);
    refetch();
    // Public catalog caches (marketing/funnel views in this app) must not
    // serve stale plans after an admin edit (review F12).
    queryClient.invalidateQueries({ queryKey: ['catalog'] });
  };

  const handleToggleActive = async (offering: AdminProductOffering) => {
    setTogglingId(offering.id);
    const action = offering.isActive ? 'deactivate' : 'activate';
    const response = await apiClient.post(
      `/api/v1/admin/products/offerings/${offering.id}/${action}`
    );
    setTogglingId(null);
    if (!response.success) {
      setTableError(
        response.error?.message || `Failed to ${action} "${offering.name}". Please try again.`
      );
      return;
    }
    setTableError(null);
    refetch();
    queryClient.invalidateQueries({ queryKey: ['catalog'] });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Product Offerings</h1>
          <p className="text-muted-foreground">
            Plans shown on the marketing site and in the purchase funnel. Deactivated plans
            disappear from public surfaces but keep their order history.
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          New Offering
        </Button>
      </div>

      {tableError && <p className="text-sm text-destructive">{tableError}</p>}

      <Card>
        <CardHeader>
          <CardTitle>All offerings</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Billing</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(offerings ?? []).map((offering) => (
                  <TableRow key={offering.id}>
                    <TableCell>{offering.displayOrder}</TableCell>
                    <TableCell>
                      <div className="font-medium">{offering.name}</div>
                      <div className="text-xs text-muted-foreground">{offering.planLabel}</div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{offering.slug}</TableCell>
                    <TableCell>
                      {formatPrice(offering.priceCents)}
                      {offering.recurringPriceCents != null && (
                        <span className="text-xs text-muted-foreground">
                          {' '}
                          then {formatPrice(offering.recurringPriceCents)}/
                          {offering.recurringInterval}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {offering.billingType === 'one_time' ? 'One-time' : 'First year + recurring'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Badge variant={offering.isActive ? 'default' : 'secondary'}>
                          {offering.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                        {offering.status === 'coming_soon' && (
                          <Badge variant="outline">Coming soon</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(offering)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={togglingId === offering.id}
                        onClick={() => handleToggleActive(offering)}
                        title={offering.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {togglingId === offering.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : offering.isActive ? (
                          <PowerOff className="h-4 w-4" />
                        ) : (
                          <Power className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {(offerings ?? []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      No offerings yet. Run the catalog seed or create one.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit offering' : 'New offering'}</DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'The slug is immutable — it is referenced by marketing deep links.'
                : 'Slug becomes part of marketing deep links (?plan=slug) and cannot be changed later.'}
            </DialogDescription>
          </DialogHeader>

          {/* Form on the left, live plan-card preview on the right (review F6);
              stacked on narrow viewports. */}
          <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_280px]">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="slug">Slug</Label>
                <Input
                  id="slug"
                  value={form.slug}
                  disabled={isEditing}
                  placeholder="core"
                  onChange={(e) => set('slug', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="planLabel">Plan label</Label>
                <Input
                  id="planLabel"
                  value={form.planLabel}
                  placeholder="CORE PLAN"
                  onChange={(e) => set('planLabel', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={form.name}
                  placeholder="Build Your Life Story"
                  onChange={(e) => set('name', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tagline">Tagline</Label>
                <Input
                  id="tagline"
                  value={form.tagline}
                  placeholder="Go deeper, chapter by chapter"
                  onChange={(e) => set('tagline', e.target.value)}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={form.description}
                  placeholder="6-month story collection pass, in the mobile app."
                  onChange={(e) => set('description', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="badge">Badge (optional)</Label>
                <Input
                  id="badge"
                  value={form.badge}
                  placeholder="SIGNATURE PLAN"
                  onChange={(e) => set('badge', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="displayOrder">Display order</Label>
                <Input
                  id="displayOrder"
                  type="number"
                  value={form.displayOrder}
                  onChange={(e) => set('displayOrder', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="price">Price (USD)</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={form.priceDollars}
                  placeholder="349"
                  onChange={(e) => set('priceDollars', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Billing type</Label>
                <Select
                  value={form.billingType}
                  onValueChange={(v) => set('billingType', v as BillingType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_time">One-time</SelectItem>
                    <SelectItem value="first_year_then_recurring">
                      First year + recurring
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Availability</Label>
                {/* Coming-soon plans stay visible on public surfaces but cannot
                  be selected or purchased (ClickUp 86bar0jc1). */}
                <Select
                  value={form.status}
                  onValueChange={(v) => set('status', v as CatalogStatus)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="coming_soon">Coming soon</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.billingType === 'first_year_then_recurring' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="recurringPrice">Recurring price (USD)</Label>
                    <Input
                      id="recurringPrice"
                      type="number"
                      step="0.01"
                      value={form.recurringPriceDollars}
                      placeholder="19"
                      onChange={(e) => set('recurringPriceDollars', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Recurring interval</Label>
                    <Select
                      value={form.recurringInterval}
                      onValueChange={(v) => set('recurringInterval', v as 'month' | 'year')}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="month">Monthly</SelectItem>
                        <SelectItem value="year">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div className="space-y-2">
                <Label htmlFor="collectionPeriod">
                  Collection period (months, blank = ongoing)
                </Label>
                <Input
                  id="collectionPeriod"
                  type="number"
                  value={form.collectionPeriodMonths}
                  placeholder="6"
                  onChange={(e) => set('collectionPeriodMonths', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="storyLimit">Story limit (blank = unlimited)</Label>
                <Input
                  id="storyLimit"
                  type="number"
                  value={form.storyLimit}
                  onChange={(e) => set('storyLimit', e.target.value)}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="features">Features (one per line, order = display order)</Label>
                <Textarea
                  id="features"
                  rows={6}
                  value={form.featuresText}
                  placeholder={'Mobile app access\nAI-guided prompts'}
                  onChange={(e) => set('featuresText', e.target.value)}
                />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input
                  id="includesOrb"
                  type="checkbox"
                  checked={form.includesOrb}
                  onChange={(e) => set('includesOrb', e.target.checked)}
                />
                <Label htmlFor="includesOrb">
                  Physical orb included (hides the orb add-on for this plan)
                </Label>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Live preview</p>
              <OfferingCardPreview form={form} />
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={
                isSaving ||
                !form.name ||
                !form.planLabel ||
                !form.priceDollars ||
                (!isEditing && !form.slug)
              }
            >
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Save changes' : 'Create offering'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Live plan-card preview rendered purely from the form state (review F6) —
 * mirrors what the marketing /products card and funnel offering card show.
 * Presentational only: no fetching, tolerant of partially filled forms.
 */
function OfferingCardPreview({ form }: { form: OfferingFormData }) {
  const priceCents = dollarsToCents(form.priceDollars);
  const recurringCents = dollarsToCents(form.recurringPriceDollars);
  const showRecurring =
    form.billingType === 'first_year_then_recurring' && Number.isFinite(recurringCents);
  const features = form.featuresText
    .split('\n')
    .map((f) => f.trim())
    .filter(Boolean);

  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {form.planLabel || 'PLAN LABEL'}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <h3 className="text-lg font-bold text-foreground">{form.name || 'Plan name'}</h3>
          {form.badge && <Badge variant="secondary">{form.badge}</Badge>}
        </div>
        {form.tagline && <p className="mt-1 text-sm text-muted-foreground">{form.tagline}</p>}

        <p className="mt-3 text-2xl font-bold text-foreground">
          {Number.isFinite(priceCents) ? formatPrice(priceCents) : '$—'}
          {showRecurring && (
            <span className="ml-1 text-sm font-normal text-muted-foreground">
              then {formatPrice(recurringCents)}/{form.recurringInterval}
            </span>
          )}
        </p>

        {form.description && (
          <p className="mt-2 text-sm text-muted-foreground">{form.description}</p>
        )}

        {features.length > 0 && (
          <ul className="mt-4 space-y-1.5">
            {features.map((feature, index) => (
              <li
                key={`${feature}-${index}`}
                className="flex items-start gap-2 text-sm text-muted-foreground"
              >
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                {feature}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
